import { keepPreviousData, queryOptions } from "@tanstack/react-query";
import { isFinalError } from "@/integrations/tanstack-query/root-provider";
import {
	isRateLimited,
	loadProduct,
	loadSteps,
	searchProducts,
} from "./loader";
import { isGenerating } from "./schema";

/** TanStack Query options for the core API. */

/** The catalogue search; `readyOnly` lists the products whose 3D guide can be opened. */
export const productsQuery = (q: string, readyOnly = false) =>
	queryOptions({
		queryKey: ["products", "list", q, readyOnly ? "ready" : "all"],
		// no abort signal: an aborted search still counts against RATE_LIMIT_SEARCH, and a remount
		// (StrictMode, navigation) would abort it and send it again; this way it is reused
		queryFn: () =>
			searchProducts(q, undefined, readyOnly ? { status: "ready" } : {}),
		// keep the previous results on screen while the next search runs
		placeholderData: keepPreviousData,
		staleTime: 30_000,
		// one search per second (RATE_LIMIT_SEARCH): wait as long as the API says, then again
		retry: (failures, error) =>
			isRateLimited(error)
				? failures < 3
				: !isFinalError(error) && failures < 3,
		retryDelay: (failures, error) =>
			isRateLimited(error) && error.retryAfterMs
				? error.retryAfterMs
				: Math.min(1000 * 2 ** failures, 30_000),
	});

export const productQuery = (slug: string) =>
	queryOptions({
		queryKey: ["products", "detail", slug],
		queryFn: ({ signal }) => loadProduct(slug, signal),
		// an unknown slug will not appear by retrying; a guide refused by the rate limit
		// (RATE_LIMIT_GUIDE) waits for its countdown (Product, GuideRateLimited)
		retry: (failures, error) => !isFinalError(error) && failures < 3,
		// the presigned URLs live S3_PRESIGN_TTL_SECONDS (1 h): refresh well before
		staleTime: 5 * 60_000,
		// follow a running generation
		refetchInterval: (query) => {
			const status = query.state.data?.generation_status;
			return status && isGenerating(status) ? 10_000 : false;
		},
		// a ready product does not change: new URLs would not be used anyway
		refetchOnWindowFocus: (query) =>
			query.state.data?.generation_status !== "ready",
	});

/**
 * `steps.json` of a product. The key is the files `version`, not the URL: the URL changes at every
 * API call, the content only when the product is published again.
 */
export const stepsQuery = (slug: string, version: string, url: string) =>
	queryOptions({
		queryKey: ["products", "steps", slug, version],
		queryFn: ({ signal }) => loadSteps(url, slug, signal),
		staleTime: Number.POSITIVE_INFINITY,
	});
